import React, { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Spin } from "antd";

/**
 * PDF.js worker — must be set in this same module (see react-pdf docs).
 * CDN avoids CRA/webpack path issues with import.meta + worker bundles.
 */
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * Renders PDF pages to canvas (no browser PDF plugin — no download/print/zoom chrome).
 * Text/annotation layers off to reduce copy and link escapes; content still draws on canvas.
 */
const CompanyPolicyPdfViewer = ({ fileUrl, onLoadError }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageWidth, setPageWidth] = useState(720);
  const wrapRef = useRef(null);

  const measure = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const w = el.getBoundingClientRect().width;
    setPageWidth(Math.max(280, Math.min(w - 8, 960)));
  }, []);

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(() => measure());
    const el = wrapRef.current;
    if (el) ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  const onDocLoad = useCallback(({ numPages: n }) => {
    setNumPages(n);
  }, []);

  const onErr = useCallback(
    (err) => {
      setNumPages(null);
      if (typeof onLoadError === "function") onLoadError(err);
    },
    [onLoadError]
  );

  if (!fileUrl) return null;

  return (
    <div
      ref={wrapRef}
      className="company-policy-pdf-inner select-none"
      onContextMenu={(e) => e.preventDefault()}
      style={{
        userSelect: "none",
        WebkitUserSelect: "none",
      }}
    >
      <Document
        file={fileUrl}
        loading={
          <div className="flex justify-center py-12">
            <Spin size="large" />
          </div>
        }
        onLoadSuccess={onDocLoad}
        onLoadError={onErr}
      >
        {numPages
          ? Array.from({ length: numPages }, (_, i) => (
              <div
                key={`policy-page-${i + 1}`}
                className="flex justify-center mb-4 last:mb-0 bg-white shadow-sm rounded overflow-hidden"
              >
                <Page
                  pageNumber={i + 1}
                  width={pageWidth}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  canvasBackground="white"
                />
              </div>
            ))
          : null}
      </Document>
    </div>
  );
};

export default CompanyPolicyPdfViewer;
